/**
 * Planning Page - Resource-based scheduling view
 * Shows week view with days as columns and subcontractors as rows
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import {
  Leaf,
  ChevronLeft,
  ChevronRight,
  Clock,
  GripVertical,
  Calendar,
  CheckCircle2,
  Filter,
  ChevronDown,
  ChevronUp,
  Plus,
  Search,
  Link,
  ArrowLeft,
  MapPin,
  User,
  Check,
  X,
  Mail,
  UserMinus,
  AlertTriangle,
  ChevronsUpDown,
  FileText
} from 'lucide-react'
import {
  format,
  addDays,
  startOfWeek,
  addWeeks,
  subWeeks,
  isSameDay,
  isToday,
  isBefore,
  startOfDay,
  endOfWeek,
  getWeek
} from 'date-fns'
import { sv } from 'date-fns/locale'
import toast from 'react-hot-toast'
import { supabase } from '../../lib/supabase'
import { isDateInUnavailabilityPeriod, type Unavailability } from '../../services/unavailability'
import { calculateAssignmentStatusV2 } from '../../services/assignmentStatusCalculatorV2'
import {
  getAssignmentStatusBadgeClassesV2,
  getAssignmentStatusNameV2,
  getAssignmentStatusColorV2
} from '../../services/statusSystemV2'
import type { AssignmentStatusV2 } from '../../types/statusTypes'
import { getHolidayForDate } from '../../utils/swedishHolidays'
import { ConfirmPlanningModal, type PlanningConfirmationResult } from '../../components/dialogs/ConfirmPlanningModal'
import { RevertToPreliminaryModal, type RevertToPreliminaryResult } from '../../components/dialogs/RevertToPreliminaryModal'
import { DateChangeRebookingModal, type DateChangeResult } from '../../components/dialogs/DateChangeRebookingModal'
import { sendSMS } from '../../services/sms'
import { SendScheduleModal } from '../../components/dialogs/SendScheduleModal'
import type { CustomerCommStatus, TgWorkStatus } from '../../types/statusTypes'
import { AssignmentDetail } from './AssignmentDetail'
import { useAuth } from '../../lib/auth'

interface Assignment {
  id: string
  assignment_number: number | null
  title: string
  description: string | null
  start_date: string
  start_time: string
  end_time: string
  status: string
  customer_id: string
  location: string | null
  customers: {
    id: string
    full_name: string
  } | null
  assignment_subcontractors: Array<{
    subcontractor_id: string
    schedule_sent_at?: string | null
    subcontractors: {
      id: string
      name: string
      color: string
      email?: string | null
      phone?: string | null
    } | null
  }>
}

interface Subcontractor {
  id: string
  name: string
  color: string
  is_active: boolean
  email?: string | null
  phone?: string | null
  company_name?: string | null
  available_days?: boolean[] | null  // [Mon, Tue, Wed, Thu, Fri, Sat, Sun]
}

type SidebarTabType = 'selected_week' | 'unplanned' | 'unassigned'

// V2 Statuses that are relevant for planning (all except 'avbruten')
const PLANNING_STATUSES: AssignmentStatusV2[] = [
  'ohanterad', 'planering', 'preliminar', 'planerad',
  'pagaende', 'granskning', 'attesterad', 'slutford'
]

// The 5 "active" statuses checked by default
const ACTIVE_STATUSES: AssignmentStatusV2[] = [
  'ohanterad', 'planering', 'preliminar', 'planerad', 'pagaende'
]

export function Planning() {
  const queryClient = useQueryClient()
  useAuth()

  // State
  const [currentWeekStart, setCurrentWeekStart] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 })
  )
  const [draggedAssignment, setDraggedAssignment] = useState<Assignment | null>(null)
  const [showBacklog, setShowBacklog] = useState(true)
  const [sidebarTab, setSidebarTab] = useState<SidebarTabType>('selected_week')
  const [collapsedSubcontractors, setCollapsedSubcontractors] = useState<Set<string>>(new Set())
  const [statusFilter, setStatusFilter] = useState<string[]>([...PLANNING_STATUSES])
  const [showStatusFilter, setShowStatusFilter] = useState(false)

  // Day and subcontractor selection
  const [selectedDays, setSelectedDays] = useState<number[]>([0, 1, 2, 3, 4]) // Mon-Fri (0=Mon, 6=Sun)
  const [selectedSubcontractors, setSelectedSubcontractors] = useState<string[]>([])
  const [showSubcontractorFilter, setShowSubcontractorFilter] = useState(false)
  const [showDayFilter, setShowDayFilter] = useState(false)

  // Move dialog state
  const [showMoveDialog, setShowMoveDialog] = useState(false)
  const [moveTarget, setMoveTarget] = useState<{
    assignment: Assignment
    targetSubId: string
    targetDate: Date
  } | null>(null)

  // Add dialog state (for + button)
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [addTarget, setAddTarget] = useState<{
    subcontractorId?: string
    date: Date
  } | null>(null)
  const [addMode, setAddMode] = useState<'select' | 'search'>('select')
  const [searchQuery, setSearchQuery] = useState('')

  // Modal state for confirm planning (green check)
  const [showPlanningModal, setShowPlanningModal] = useState(false)
  const [planningModalAssignment, setPlanningModalAssignment] = useState<Assignment | null>(null)
  const [planningModalTgInfo, setPlanningModalTgInfo] = useState<Array<{
    id: string
    subcontractorId: string
    name: string
    phone: string | null
  }>>([])

  // Modal state for revert to preliminary (red X)
  const [showRevertModal, setShowRevertModal] = useState(false)
  const [revertModalAssignment, setRevertModalAssignment] = useState<Assignment | null>(null)
  const [revertModalCustomerInfo, setRevertModalCustomerInfo] = useState<{
    name: string
    status: CustomerCommStatus
  } | null>(null)
  const [revertModalTgInfo, setRevertModalTgInfo] = useState<Array<{
    id: string
    subcontractorId: string
    name: string
    phone: string | null
    currentStatus: TgWorkStatus
  }>>([])
  const [revertModalCustomerId, setRevertModalCustomerId] = useState<string | null>(null)

  // Modal state for date change on planned assignments
  const [showDateChangeModal, setShowDateChangeModal] = useState(false)
  const [dateChangeModalData, setDateChangeModalData] = useState<{
    assignment: Assignment
    originalDate: string
    newDate: string
    newSubcontractorId: string | null
    mode: 'move' | 'add'
    customerInfo: { name: string; status: CustomerCommStatus }
    tgList: Array<{
      id: string
      subcontractorId: string
      name: string
      phone: string | null
      currentStatus: TgWorkStatus
    }>
  } | null>(null)

  // Modal state for sending schedule
  const [showScheduleModal, setShowScheduleModal] = useState(false)

  // Modal state for unavailability warning
  const [showUnavailableWarning, setShowUnavailableWarning] = useState(false)
  const [unavailableDropData, setUnavailableDropData] = useState<{
    assignment: Assignment
    subcontractorId: string
    subcontractorName: string
    date: Date
    dayName: string
    reason?: string
  } | null>(null)

  // Assignment detail modal state
  const [modalAssignmentId, setModalAssignmentId] = useState<string | null>(null)
  const [modalPrefillSubId, setModalPrefillSubId] = useState<string | undefined>(undefined)
  const [modalPrefillDate, setModalPrefillDate] = useState<string | undefined>(undefined)

  // Week note state
  const [weekNote, setWeekNote] = useState('')
  const [weekNoteId, setWeekNoteId] = useState<string | null>(null)
  const [showWeekNote, setShowWeekNote] = useState(false)
  const [weekNoteSaved, setWeekNoteSaved] = useState(false)

  const displayWeekStart = currentWeekStart
  const currentWeekNumber = getWeek(displayWeekStart, { weekStartsOn: 1 })
  const allWeekDays = Array.from({ length: 7 }, (_, i) => addDays(displayWeekStart, i)) // All 7 days
  const weekDays = allWeekDays.filter((_, index) => selectedDays.includes(index)) // Only selected days

  // Fetch subcontractors
  const { data: subcontractors = [] } = useQuery({
    queryKey: ['subcontractors'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subcontractors')
        .select('*')
        .eq('is_active', true)
        .order('name')
      if (error) throw error
      return data as Subcontractor[]
    }
  })

  // Initialize selected subcontractors when data loads
  useMemo(() => {
    if (subcontractors.length > 0 && selectedSubcontractors.length === 0) {
      setSelectedSubcontractors(subcontractors.map(s => s.id))
    }
  }, [subcontractors])

  // Filter visible subcontractors
  const visibleSubcontractors = useMemo(() => {
    if (selectedSubcontractors.length === 0) return subcontractors
    return subcontractors.filter(s => selectedSubcontractors.includes(s.id))
  }, [subcontractors, selectedSubcontractors])

  // Fetch unavailability data for all visible subcontractors
  const { data: unavailabilityData = [] } = useQuery({
    queryKey: ['subcontractor-unavailability', visibleSubcontractors.map(s => s.id).join(',')],
    queryFn: async () => {
      if (visibleSubcontractors.length === 0) return []
      const { data, error } = await supabase
        .from('subcontractor_unavailability')
        .select('*')
        .in('subcontractor_id', visibleSubcontractors.map(s => s.id))
      if (error) throw error
      return data as Unavailability[]
    },
    enabled: visibleSubcontractors.length > 0
  })

  // Day names for UI
  const dayNames = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön']

  // Toggle functions
  const toggleDay = (dayIndex: number) => {
    setSelectedDays(prev =>
      prev.includes(dayIndex)
        ? prev.filter(d => d !== dayIndex)
        : [...prev, dayIndex].sort()
    )
  }

  const toggleSubcontractor = (subId: string) => {
    setSelectedSubcontractors(prev =>
      prev.includes(subId)
        ? prev.filter(id => id !== subId)
        : [...prev, subId]
    )
  }

  // Fetch assignments for the displayed week
  const { data: assignments = [], isLoading } = useQuery({
    queryKey: ['planning-assignments', displayWeekStart.toISOString()],
    queryFn: async () => {
      const startDate = format(displayWeekStart, 'yyyy-MM-dd')
      const endDate = format(addDays(displayWeekStart, 6), 'yyyy-MM-dd')

      const { data, error } = await supabase
        .from('assignments')
        .select(`
          *,
          customers(id, full_name),
          assignment_subcontractors(
            subcontractor_id,
            schedule_sent_at,
            subcontractors(id, name, color, phone)
          )
        `)
        .in('status', PLANNING_STATUSES)
        .gte('start_date', startDate)
        .lte('start_date', endDate)
        .order('start_time')

      if (error) throw error
      return data as Assignment[]
    }
  })

  // Calculate schedule status per TG for current week
  const tgScheduleStatus = useMemo(() => {
    const status: Record<string, { totalAssignments: number; sentCount: number; allSent: boolean }> = {}

    visibleSubcontractors.forEach(sub => {
      const subAssignments = assignments.filter(a =>
        a.assignment_subcontractors?.some(as => as.subcontractor_id === sub.id)
      )
      const sentCount = subAssignments.filter(a =>
        a.assignment_subcontractors?.find(as => as.subcontractor_id === sub.id)?.schedule_sent_at
      ).length

      status[sub.id] = {
        totalAssignments: subAssignments.length,
        sentCount,
        allSent: subAssignments.length > 0 && sentCount === subAssignments.length
      }
    })

    return status
  }, [assignments, visibleSubcontractors])

  // Fetch all assignments for sidebar (all planning statuses)
  const { data: sidebarAssignments = [] } = useQuery({
    queryKey: ['sidebar-assignments'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('assignments')
        .select(`
          *,
          customers(id, full_name),
          assignment_subcontractors(
            subcontractor_id,
            subcontractors(id, name, color)
          )
        `)
        .in('status', PLANNING_STATUSES)
        .order('start_date', { ascending: true })
        .order('start_time', { ascending: true })

      if (error) throw error
      return data as Assignment[]
    }
  })

  // Group sidebar assignments synced with selected week
  const groupedSidebarAssignments = useMemo(() => {
    const selectedWeekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 1 })

    const groups: Record<SidebarTabType, Assignment[]> = {
      selected_week: [],
      unplanned: [],
      unassigned: []
    }

    sidebarAssignments.forEach(a => {
      const hasSubcontractors = (a.assignment_subcontractors?.length ?? 0) > 0

      // Unassigned: no subcontractors regardless of date
      if (!hasSubcontractors) {
        groups.unassigned.push(a)
      }

      // Unplanned: no date
      if (!a.start_date) {
        groups.unplanned.push(a)
        return
      }

      const assignmentDate = new Date(a.start_date)

      // Selected week
      if (assignmentDate >= startOfDay(currentWeekStart) && assignmentDate <= selectedWeekEnd) {
        groups.selected_week.push(a)
      }
    })

    return groups
  }, [sidebarAssignments, currentWeekStart])

  // Filtered counts for sidebar tabs (respects status filter)
  const filteredSidebarCounts = useMemo(() => {
    const filterByStatus = (items: Assignment[]) => {
      if (statusFilter.length === 0) return items.length
      return items.filter(a => statusFilter.includes(a.status)).length
    }
    return {
      selected_week: filterByStatus(groupedSidebarAssignments.selected_week),
      unplanned: filterByStatus(groupedSidebarAssignments.unplanned),
      unassigned: filterByStatus(groupedSidebarAssignments.unassigned)
    }
  }, [groupedSidebarAssignments, statusFilter])

  // Filtered assignments (use sidebar filter for grid too, plus TG filter)
  const filteredAssignments = useMemo(() => {
    let result = assignments

    // Filter by status if set
    if (statusFilter.length > 0) {
      result = result.filter(a => statusFilter.includes(a.status))
    }

    // Filter by selected subcontractors (TG filter)
    if (selectedSubcontractors.length > 0 && selectedSubcontractors.length < subcontractors.length) {
      result = result.filter(a => {
        const assignedSubs = a.assignment_subcontractors?.map(as => as.subcontractor_id) || []
        // Show assignment if any of its subcontractors is in the selected list
        return assignedSubs.some(subId => selectedSubcontractors.includes(subId))
      })
    }

    return result
  }, [assignments, statusFilter, selectedSubcontractors, subcontractors.length])

  // Filter assignments for search in add dialog (from unplanned sidebar assignments)
  const searchableAssignments = useMemo(() => {
    const unplanned = groupedSidebarAssignments.unplanned
    if (!searchQuery.trim()) return unplanned.slice(0, 10)
    const query = searchQuery.toLowerCase()
    return unplanned.filter(a =>
      a.title.toLowerCase().includes(query) ||
      a.customers?.full_name?.toLowerCase().includes(query) ||
      (a.assignment_number && a.assignment_number.toString().includes(query))
    ).slice(0, 10)
  }, [groupedSidebarAssignments.unplanned, searchQuery])

  // Get assignments for a specific subcontractor and date
  const getAssignmentsForCell = (subcontractorId: string | null, date: Date) => {
    return filteredAssignments.filter(assignment => {
      if (!isSameDay(new Date(assignment.start_date), date)) return false

      const assignedSubs = assignment.assignment_subcontractors?.map(as => as.subcontractor_id) || []

      if (subcontractorId === null) {
        return assignedSubs.length === 0
      }

      return assignedSubs.includes(subcontractorId)
    })
  }

  // Calculate total hours for a subcontractor on a specific day
  const getHoursForCell = (subcontractorId: string, date: Date) => {
    const cellAssignments = getAssignmentsForCell(subcontractorId, date)
    let totalMinutes = 0

    cellAssignments.forEach(a => {
      if (!a.start_time || !a.end_time) return // Skip assignments without times
      const [startH, startM] = a.start_time.split(':').map(Number)
      const [endH, endM] = a.end_time.split(':').map(Number)
      totalMinutes += (endH * 60 + endM) - (startH * 60 + startM)
    })

    return totalMinutes / 60
  }

  // Check if a subcontractor is available on a specific date
  // Returns { available: boolean, reason?: string }
  const checkSubcontractorAvailability = (subcontractorId: string, date: Date): { available: boolean; reason?: string } => {
    const sub = subcontractors.find(s => s.id === subcontractorId)

    // Check unavailability periods first (they have specific reasons)
    const unavailabilityCheck = isDateInUnavailabilityPeriod(subcontractorId, date, unavailabilityData)
    if (unavailabilityCheck.unavailable) {
      return { available: false, reason: unavailabilityCheck.reason }
    }

    // Check weekly availability
    if (!sub?.available_days) return { available: true }

    // Get day of week: 0=Sun, 1=Mon, ..., 6=Sat
    // Convert to our format: 0=Mon, 1=Tue, ..., 6=Sun
    const jsDay = date.getDay()
    const dayIndex = jsDay === 0 ? 6 : jsDay - 1

    const isAvailable = sub.available_days[dayIndex] ?? true
    return { available: isAvailable }
  }

  // Backwards compatible helper (used in grid cells)
  const isSubcontractorAvailable = (subcontractorId: string, date: Date): boolean => {
    return checkSubcontractorAvailability(subcontractorId, date).available
  }

  // Mutation to update assignment
  const updateAssignmentMutation = useMutation({
    mutationFn: async ({
      assignmentId,
      subcontractorId,
      newDate,
      newStatus,
      mode = 'move'
    }: {
      assignmentId: string
      subcontractorId?: string | null
      newDate?: string
      newStatus?: string
      mode?: 'move' | 'add' | 'remove_all'
    }) => {
      // If only updating status, do that directly
      if (newStatus && !subcontractorId && !newDate) {
        await supabase
          .from('assignments')
          .update({ status: newStatus, updated_at: new Date().toISOString() })
          .eq('id', assignmentId)
        return
      }

      // Remove all TGs mode
      if (mode === 'remove_all') {
        await supabase
          .from('assignment_subcontractors')
          .delete()
          .eq('assignment_id', assignmentId)

        // Recalculate status without any TGs
        const { data: assignment } = await supabase
          .from('assignments')
          .select('start_date, start_time, status')
          .eq('id', assignmentId)
          .single()

        if (assignment) {
          const newStatus = calculateAssignmentStatusV2({
            status: assignment.status as AssignmentStatusV2,
            start_date: assignment.start_date || '',
            start_time: assignment.start_time || '',
            subcontractor_ids: []
          })

          await supabase
            .from('assignments')
            .update({ status: newStatus, updated_at: new Date().toISOString() })
            .eq('id', assignmentId)
        }
        return
      }

      // Get current subcontractors
      const { data: currentSubs } = await supabase
        .from('assignment_subcontractors')
        .select('subcontractor_id')
        .eq('assignment_id', assignmentId)

      const currentSubIds = currentSubs?.map(s => s.subcontractor_id) || []

      if (mode === 'move') {
        // Only replace subcontractors if a new one is specified AND it's different
        // If no subcontractorId or already assigned, just update date (don't touch TGs)
        if (subcontractorId && !currentSubIds.includes(subcontractorId)) {
          // Replace all subcontractors with the new one
          await supabase
            .from('assignment_subcontractors')
            .delete()
            .eq('assignment_id', assignmentId)

          await supabase
            .from('assignment_subcontractors')
            .insert({
              assignment_id: assignmentId,
              subcontractor_id: subcontractorId
            })
        }
        // If subcontractorId is null or already assigned, we just update the date (handled below)
      } else if (mode === 'add' && subcontractorId) {
        // Add to existing subcontractors (if not already assigned)
        if (!currentSubIds.includes(subcontractorId)) {
          await supabase
            .from('assignment_subcontractors')
            .insert({
              assignment_id: assignmentId,
              subcontractor_id: subcontractorId
            })
        }
      }

      // Update date if provided
      if (newDate) {
        await supabase
          .from('assignments')
          .update({
            start_date: newDate,
            updated_at: new Date().toISOString()
          })
          .eq('id', assignmentId)
      }

      // Get final subcontractors for status calculation
      const { data: finalSubs } = await supabase
        .from('assignment_subcontractors')
        .select('subcontractor_id')
        .eq('assignment_id', assignmentId)

      const finalSubIds = finalSubs?.map(s => s.subcontractor_id) || []

      // Recalculate status
      const { data: assignment } = await supabase
        .from('assignments')
        .select('customer_id, start_date, start_time, status')
        .eq('id', assignmentId)
        .single()

      if (assignment) {
        const newStatus = calculateAssignmentStatusV2({
          status: assignment.status as AssignmentStatusV2,
          start_date: newDate || assignment.start_date || '',
          start_time: assignment.start_time || '',
          subcontractor_ids: finalSubIds
        })

        await supabase
          .from('assignments')
          .update({ status: newStatus, updated_at: new Date().toISOString() })
          .eq('id', assignmentId)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning-assignments'] })
      queryClient.invalidateQueries({ queryKey: ['sidebar-assignments'] })
      toast.success('Uppdrag uppdaterat!')
    },
    onError: () => {
      toast.error('Kunde inte uppdatera uppdrag')
    }
  })

  // Check if assignment needs date change modal (planerad or pagaende)
  const needsDateChangeModal = (status: string) => {
    return status === 'planerad' || status === 'pagaende'
  }

  // Fetch data for date change modal
  const fetchDateChangeModalData = async (
    assignment: Assignment
  ) => {
    // Fetch customer info
    let customerInfo = { name: 'Ingen kund', status: 'ej_kontaktad' as CustomerCommStatus }
    if (assignment.customer_id) {
      const { data: customer } = await supabase
        .from('customers')
        .select('full_name, comm_status')
        .eq('id', assignment.customer_id)
        .single()

      if (customer) {
        customerInfo = {
          name: customer.full_name || 'Ingen kund',
          status: (customer.comm_status as CustomerCommStatus) || 'ej_kontaktad'
        }
      }
    }

    // Fetch TG info with phone numbers and work status
    const tgIds = assignment.assignment_subcontractors
      ?.map(as => as.subcontractor_id)
      .filter(Boolean) || []

    let tgList: Array<{
      id: string
      subcontractorId: string
      name: string
      phone: string | null
      currentStatus: TgWorkStatus
    }> = []

    if (tgIds.length > 0) {
      const { data: tgData } = await supabase
        .from('subcontractors')
        .select('id, name, phone')
        .in('id', tgIds)

      const { data: asData } = await supabase
        .from('assignment_subcontractors')
        .select('subcontractor_id, work_status')
        .eq('assignment_id', assignment.id)

      tgList = tgIds.map(tgId => {
        const tgDetails = tgData?.find(t => t.id === tgId)
        const asDetails = asData?.find(as => as.subcontractor_id === tgId)
        return {
          id: tgId,
          subcontractorId: tgId,
          name: tgDetails?.name || 'Okänd',
          phone: tgDetails?.phone || null,
          currentStatus: (asDetails?.work_status as TgWorkStatus) || 'tilldelad'
        }
      })
    }

    return { customerInfo, tgList }
  }

  // Handle drop
  const handleDrop = async (subcontractorId: string | null, date: Date) => {
    if (!draggedAssignment) return

    // Block moving completed or cancelled assignments
    if (draggedAssignment.status === 'slutford' || draggedAssignment.status === 'avbruten') {
      toast.error('Kan inte flytta slutförda eller avbrutna uppdrag')
      setDraggedAssignment(null)
      return
    }

    // Warn if dropping on an unavailable day - show modal
    if (subcontractorId) {
      const availability = checkSubcontractorAvailability(subcontractorId, date)
      if (!availability.available) {
        const sub = subcontractors.find(s => s.id === subcontractorId)
        const dayNames = ['söndag', 'måndag', 'tisdag', 'onsdag', 'torsdag', 'fredag', 'lördag']
        const dayName = dayNames[date.getDay()]
        setUnavailableDropData({
          assignment: draggedAssignment,
          subcontractorId,
          subcontractorName: sub?.name || 'TG',
          date,
          dayName,
          reason: availability.reason
        })
        setShowUnavailableWarning(true)
        setDraggedAssignment(null)
        return
      }
    }

    const newDateStr = format(date, 'yyyy-MM-dd')
    const existingSubs = draggedAssignment.assignment_subcontractors || []
    const hasExistingSubs = existingSubs.length > 0
    const dateIsChanging = draggedAssignment.start_date !== newDateStr

    // Check if this is a planned assignment that needs special handling
    if (needsDateChangeModal(draggedAssignment.status)) {
      // If date is changing, show date change modal
      if (dateIsChanging) {
        const { customerInfo, tgList } = await fetchDateChangeModalData(draggedAssignment)

        setDateChangeModalData({
          assignment: draggedAssignment,
          originalDate: draggedAssignment.start_date,
          newDate: newDateStr,
          newSubcontractorId: subcontractorId,
          mode: 'move',
          customerInfo,
          tgList
        })
        setShowDateChangeModal(true)
        setDraggedAssignment(null)
        return
      }

      // If adding a new TG to a planned assignment (no date change)
      if (subcontractorId && hasExistingSubs) {
        const alreadyAssigned = existingSubs.some(s => s.subcontractor_id === subcontractorId)
        if (!alreadyAssigned) {
          // Show move/add dialog first, then handle in handleMoveDialogAction
          setMoveTarget({
            assignment: draggedAssignment,
            targetSubId: subcontractorId,
            targetDate: date
          })
          setShowMoveDialog(true)
          setDraggedAssignment(null)
          return
        }
      }
    }

    // For preliminar or earlier, or same date on planned: proceed normally

    // If dropping on a subcontractor and assignment already has subcontractors, show dialog
    if (subcontractorId && hasExistingSubs) {
      const alreadyAssigned = existingSubs.some(s => s.subcontractor_id === subcontractorId)

      if (!alreadyAssigned) {
        setMoveTarget({
          assignment: draggedAssignment,
          targetSubId: subcontractorId,
          targetDate: date
        })
        setShowMoveDialog(true)
        setDraggedAssignment(null)
        return
      }
    }

    // Direct move (no existing subs, or dropping on unassigned row, or same sub)
    updateAssignmentMutation.mutate({
      assignmentId: draggedAssignment.id,
      subcontractorId,
      newDate: newDateStr,
      mode: 'move'
    })

    setDraggedAssignment(null)
  }

  // Handle removing all TGs from an assignment
  const handleRemoveAllTGs = () => {
    if (!draggedAssignment) return

    // Block for completed or cancelled assignments
    if (draggedAssignment.status === 'slutford' || draggedAssignment.status === 'avbruten') {
      toast.error('Kan inte ändra slutförda eller avbrutna uppdrag')
      setDraggedAssignment(null)
      return
    }

    // Check if there are any TGs to remove
    if (!draggedAssignment.assignment_subcontractors?.length) {
      toast.error('Uppdraget har inga tilldelade trädgårdsmästare')
      setDraggedAssignment(null)
      return
    }

    updateAssignmentMutation.mutate({
      assignmentId: draggedAssignment.id,
      mode: 'remove_all'
    })

    toast.success('Alla trädgårdsmästare borttagna från uppdraget')
    setDraggedAssignment(null)
  }

  // Handle unavailability warning confirmation
  const handleUnavailableConfirm = async () => {
    if (!unavailableDropData) return

    setShowUnavailableWarning(false)

    // Continue with the drop - use assignment from unavailableDropData
    const { assignment, subcontractorId, date } = unavailableDropData
    const newDateStr = format(date, 'yyyy-MM-dd')
    const existingSubs = assignment.assignment_subcontractors || []
    const hasExistingSubs = existingSubs.length > 0
    const dateIsChanging = assignment.start_date !== newDateStr

    // Check if this is a planned assignment that needs special handling
    if (needsDateChangeModal(assignment.status)) {
      if (dateIsChanging) {
        const { customerInfo, tgList } = await fetchDateChangeModalData(assignment)
        setDateChangeModalData({
          assignment,
          originalDate: assignment.start_date,
          newDate: newDateStr,
          newSubcontractorId: subcontractorId,
          mode: 'move',
          customerInfo,
          tgList
        })
        setShowDateChangeModal(true)
        setUnavailableDropData(null)
        return
      }

      if (subcontractorId && hasExistingSubs) {
        const alreadyAssigned = existingSubs.some(s => s.subcontractor_id === subcontractorId)
        if (!alreadyAssigned) {
          setMoveTarget({
            assignment,
            targetSubId: subcontractorId,
            targetDate: date
          })
          setShowMoveDialog(true)
          setUnavailableDropData(null)
          return
        }
      }
    }

    // For preliminar or earlier: proceed normally
    if (subcontractorId && hasExistingSubs) {
      const alreadyAssigned = existingSubs.some(s => s.subcontractor_id === subcontractorId)
      if (!alreadyAssigned) {
        setMoveTarget({
          assignment,
          targetSubId: subcontractorId,
          targetDate: date
        })
        setShowMoveDialog(true)
        setUnavailableDropData(null)
        return
      }
    }

    // Direct move
    updateAssignmentMutation.mutate({
      assignmentId: assignment.id,
      subcontractorId,
      newDate: newDateStr,
      mode: 'move'
    })

    setUnavailableDropData(null)
  }

  const handleUnavailableCancel = () => {
    setShowUnavailableWarning(false)
    setUnavailableDropData(null)
  }

  // Handle move dialog actions
  const handleMoveDialogAction = async (mode: 'move' | 'add') => {
    if (!moveTarget) return

    const newDateStr = format(moveTarget.targetDate, 'yyyy-MM-dd')

    // If adding a TG to a planned assignment, show date change modal for confirmation/SMS
    if (mode === 'add' && needsDateChangeModal(moveTarget.assignment.status)) {
      const { customerInfo, tgList } = await fetchDateChangeModalData(moveTarget.assignment)

      setDateChangeModalData({
        assignment: moveTarget.assignment,
        originalDate: moveTarget.assignment.start_date,
        newDate: newDateStr,
        newSubcontractorId: moveTarget.targetSubId,
        mode: 'add',
        customerInfo,
        tgList
      })
      setShowMoveDialog(false)
      setMoveTarget(null)
      setShowDateChangeModal(true)
      return
    }

    // Otherwise proceed normally
    updateAssignmentMutation.mutate({
      assignmentId: moveTarget.assignment.id,
      subcontractorId: moveTarget.targetSubId,
      newDate: newDateStr,
      mode
    })

    setShowMoveDialog(false)
    setMoveTarget(null)
  }

  const closeMoveDialog = () => {
    setShowMoveDialog(false)
    setMoveTarget(null)
  }

  // Navigation
  const navigatePrevious = () => {
    setCurrentWeekStart(prev => subWeeks(prev, 1))
  }

  const navigateNext = () => {
    setCurrentWeekStart(prev => addWeeks(prev, 1))
  }

  const goToToday = () => {
    setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))
  }

  // Load week note when week changes
  useEffect(() => {
    const loadWeekNote = async () => {
      const weekStr = format(currentWeekStart, 'yyyy-MM-dd')
      const { data } = await supabase
        .from('planning_week_notes')
        .select('*')
        .eq('week_start', weekStr)
        .maybeSingle()
      if (data) {
        setWeekNote(data.content)
        setWeekNoteId(data.id)
      } else {
        setWeekNote('')
        setWeekNoteId(null)
      }
    }
    loadWeekNote()
  }, [currentWeekStart])

  // Save week note on blur (upsert to avoid 409 conflict on unique week_start)
  const saveWeekNote = async () => {
    const weekStr = format(currentWeekStart, 'yyyy-MM-dd')
    try {
      if (!weekNote.trim()) {
        // Delete if content was cleared
        if (weekNoteId) {
          await supabase.from('planning_week_notes').delete().eq('id', weekNoteId)
          setWeekNoteId(null)
        }
      } else {
        // Upsert handles both insert and update, avoids conflict on existing week_start
        const { data, error } = await supabase.from('planning_week_notes')
          .upsert(
            {
              ...(weekNoteId ? { id: weekNoteId } : {}),
              week_start: weekStr,
              content: weekNote,
              updated_at: new Date().toISOString()
            },
            { onConflict: 'week_start' }
          )
          .select().single()
        if (error) throw error
        if (data) setWeekNoteId(data.id)
      }
      setWeekNoteSaved(true)
      setTimeout(() => setWeekNoteSaved(false), 2000)
    } catch (error) {
      console.error('Error saving week note:', error)
      toast.error('Kunde inte spara veckonotering')
    }
  }

  // Open assignment detail in modal
  const openAssignmentDetail = (assignmentId: string) => {
    setModalAssignmentId(assignmentId)
  }

  // Handle opening the planning confirmation modal (green check)
  const handleOpenPlanningModal = useCallback(async (assignment: Assignment) => {
    // Fetch phone numbers for assigned TGs
    const tgIds = assignment.assignment_subcontractors
      ?.map(as => as.subcontractor_id)
      .filter(Boolean) || []

    if (tgIds.length === 0) {
      toast.error('Uppdraget har ingen tilldelad trädgårdsmästare')
      return
    }

    // Fetch subcontractors with phone numbers
    const { data: tgData } = await supabase
      .from('subcontractors')
      .select('id, name, phone')
      .in('id', tgIds)

    // Build TG info list
    const tgInfoList = assignment.assignment_subcontractors?.map(as => {
      const tgDetails = tgData?.find(t => t.id === as.subcontractor_id)
      return {
        id: as.subcontractor_id, // Use subcontractor_id as the key
        subcontractorId: as.subcontractor_id,
        name: tgDetails?.name || as.subcontractors?.name || 'Okänd',
        phone: tgDetails?.phone || null
      }
    }).filter(tg => tg.subcontractorId) || []

    setPlanningModalAssignment(assignment)
    setPlanningModalTgInfo(tgInfoList)
    setShowPlanningModal(true)
  }, [])

  // Handle confirm planning (from modal)
  const handleConfirmPlanning = useCallback(async (result: PlanningConfirmationResult) => {
    if (!planningModalAssignment) return

    // Update assignment status to 'planerad'
    await supabase
      .from('assignments')
      .update({
        status: 'planerad',
        updated_at: new Date().toISOString()
      })
      .eq('id', planningModalAssignment.id)

    // Send SMS to selected TGs
    const smsPromises = planningModalTgInfo
      .filter(tg => tg.phone && result.sendSmsTo[tg.id])
      .map(async tg => {
        const identifier = planningModalAssignment.assignment_number
          ? `#${planningModalAssignment.assignment_number} ${planningModalAssignment.title}`
          : planningModalAssignment.title
        const dateTime = `${planningModalAssignment.start_date}${planningModalAssignment.start_time ? ` kl ${planningModalAssignment.start_time.substring(0, 5)}` : ''}`
        const addressPart = planningModalAssignment.location ? `${planningModalAssignment.location}, ` : ''
        const extra = result.extraInfo[tg.id]?.trim()

        let message = `Du är tilldelad uppdraget ${identifier}. ${addressPart}${dateTime}.`
        if (extra) {
          message += ` ${extra}`
        }

        return sendSMS({
          message,
          recipient_type: 'subcontractor',
          recipient_id: tg.subcontractorId,
          recipient_name: tg.name,
          recipient_phone: tg.phone!,
          assignment_id: planningModalAssignment.id
        })
      })

    await Promise.allSettled(smsPromises)

    // Invalidate queries
    queryClient.invalidateQueries({ queryKey: ['planning-assignments'] })
    queryClient.invalidateQueries({ queryKey: ['sidebar-assignments'] })

    toast.success('Uppdraget är nu planerat!')

    // Reset modal state
    setShowPlanningModal(false)
    setPlanningModalAssignment(null)
    setPlanningModalTgInfo([])
  }, [planningModalAssignment, planningModalTgInfo, queryClient])

  // Handle opening the revert to preliminary modal (red X)
  const handleOpenRevertModal = useCallback(async (assignment: Assignment) => {
    // Fetch customer info
    let customerInfo = { name: 'Ingen kund', status: 'ej_kontaktad' as CustomerCommStatus }
    if (assignment.customer_id) {
      const { data: customer } = await supabase
        .from('customers')
        .select('full_name, comm_status')
        .eq('id', assignment.customer_id)
        .single()

      if (customer) {
        customerInfo = {
          name: customer.full_name || 'Ingen kund',
          status: (customer.comm_status as CustomerCommStatus) || 'ej_kontaktad'
        }
      }
    }

    // Fetch TG info with phone numbers and work status
    const tgIds = assignment.assignment_subcontractors
      ?.map(as => as.subcontractor_id)
      .filter(Boolean) || []

    let tgInfoList: Array<{
      id: string
      subcontractorId: string
      name: string
      phone: string | null
      currentStatus: TgWorkStatus
    }> = []

    if (tgIds.length > 0) {
      // Get subcontractor details
      const { data: tgData } = await supabase
        .from('subcontractors')
        .select('id, name, phone')
        .in('id', tgIds)

      // Get assignment_subcontractors with work status
      const { data: asData } = await supabase
        .from('assignment_subcontractors')
        .select('subcontractor_id, work_status')
        .eq('assignment_id', assignment.id)

      tgInfoList = tgIds.map(tgId => {
        const tgDetails = tgData?.find(t => t.id === tgId)
        const asDetails = asData?.find(as => as.subcontractor_id === tgId)
        return {
          id: tgId,
          subcontractorId: tgId,
          name: tgDetails?.name || 'Okänd',
          phone: tgDetails?.phone || null,
          currentStatus: (asDetails?.work_status as TgWorkStatus) || 'tilldelad'
        }
      })
    }

    setRevertModalAssignment(assignment)
    setRevertModalCustomerInfo(customerInfo)
    setRevertModalCustomerId(assignment.customer_id)
    setRevertModalTgInfo(tgInfoList)
    setShowRevertModal(true)
  }, [])

  // Handle confirm revert to preliminary (from modal)
  const handleConfirmRevert = useCallback(async (result: RevertToPreliminaryResult) => {
    if (!revertModalAssignment) return

    // Update assignment status to 'preliminar'
    await supabase
      .from('assignments')
      .update({
        status: 'preliminar',
        updated_at: new Date().toISOString()
      })
      .eq('id', revertModalAssignment.id)

    // Update customer status if needed
    if (result.customerAction !== 'keep' && revertModalCustomerId) {
      const newCustomerStatus = result.customerAction === 'reset_to_ej_kontaktad'
        ? 'ej_kontaktad'
        : 'informerad'

      await supabase
        .from('customers')
        .update({ comm_status: newCustomerStatus })
        .eq('id', revertModalCustomerId)
    }

    // Update TG work status to 'preliminar_tilldelad'
    if (revertModalTgInfo.length > 0) {
      await supabase
        .from('assignment_subcontractors')
        .update({ work_status: 'preliminar_tilldelad' })
        .eq('assignment_id', revertModalAssignment.id)
    }

    // Send SMS to selected TGs
    const smsPromises = revertModalTgInfo
      .filter(tg => tg.phone && result.sendSmsTo[tg.id])
      .map(async tg => {
        const identifier = revertModalAssignment.assignment_number
          ? `#${revertModalAssignment.assignment_number} ${revertModalAssignment.title}`
          : revertModalAssignment.title
        const dateTime = revertModalAssignment.start_date
          ? (revertModalAssignment.start_time
            ? `${revertModalAssignment.start_date} kl ${revertModalAssignment.start_time.substring(0, 5)}`
            : revertModalAssignment.start_date)
          : ''
        const extra = result.extraMessages[tg.id]?.trim()

        let message = `Uppdraget ${identifier}${dateTime ? ` (planerat ${dateTime})` : ''} har ändrats till preliminär planering.`
        if (extra) {
          message += ` ${extra}`
        } else {
          message += ' Vi återkopplar när vi vet mera.'
        }

        return sendSMS({
          message,
          recipient_type: 'subcontractor',
          recipient_id: tg.subcontractorId,
          recipient_name: tg.name,
          recipient_phone: tg.phone!,
          assignment_id: revertModalAssignment.id
        })
      })

    await Promise.allSettled(smsPromises)

    // Invalidate queries
    queryClient.invalidateQueries({ queryKey: ['planning-assignments'] })
    queryClient.invalidateQueries({ queryKey: ['sidebar-assignments'] })

    toast.success('Uppdraget är nu preliminärt')

    // Reset modal state
    setShowRevertModal(false)
    setRevertModalAssignment(null)
    setRevertModalCustomerInfo(null)
    setRevertModalCustomerId(null)
    setRevertModalTgInfo([])
  }, [revertModalAssignment, revertModalCustomerId, revertModalTgInfo, queryClient])

  // Handle confirm date change (from DateChangeRebookingModal)
  const handleConfirmDateChange = useCallback(async (result: DateChangeResult) => {
    if (!dateChangeModalData) return

    const { assignment, newDate, newSubcontractorId, mode } = dateChangeModalData

    // Update assignment date and potentially status
    const updateData: Record<string, unknown> = {
      start_date: newDate,
      updated_at: new Date().toISOString()
    }

    // If resetting to preliminar
    if (result.resetToPreliminar) {
      updateData.status = 'preliminar'
    }

    // If reverting from pågående to planerad (moving to future date)
    if (result.revertToPlanned) {
      updateData.status = 'planerad'
    }

    await supabase
      .from('assignments')
      .update(updateData)
      .eq('id', assignment.id)

    // Handle customer status
    if (result.customerAction !== 'keep' && assignment.customer_id) {
      const newCustomerStatus = result.customerAction === 'reset_to_ej_kontaktad'
        ? 'ej_kontaktad'
        : 'informerad'

      await supabase
        .from('customers')
        .update({ comm_status: newCustomerStatus })
        .eq('id', assignment.customer_id)
    }

    // Handle TG actions
    for (const tg of dateChangeModalData.tgList) {
      const action = result.tgActions[tg.id]
      if (action?.action === 'remove') {
        // Remove TG from assignment
        await supabase
          .from('assignment_subcontractors')
          .delete()
          .eq('assignment_id', assignment.id)
          .eq('subcontractor_id', tg.subcontractorId)
      } else if (result.resetToPreliminar) {
        // Reset TG to preliminar_tilldelad
        await supabase
          .from('assignment_subcontractors')
          .update({ work_status: 'preliminar_tilldelad' })
          .eq('assignment_id', assignment.id)
          .eq('subcontractor_id', tg.subcontractorId)
      } else if (result.revertToPlanned && tg.currentStatus === 'ej_rapporterad') {
        // Revert ej_rapporterad TG back to tilldelad
        await supabase
          .from('assignment_subcontractors')
          .update({ work_status: 'tilldelad' })
          .eq('assignment_id', assignment.id)
          .eq('subcontractor_id', tg.subcontractorId)
      }
    }

    // If adding a new TG
    if (mode === 'add' && newSubcontractorId) {
      // Check if not already assigned
      const alreadyAssigned = dateChangeModalData.tgList.some(
        tg => tg.subcontractorId === newSubcontractorId
      )
      if (!alreadyAssigned) {
        await supabase
          .from('assignment_subcontractors')
          .insert({
            assignment_id: assignment.id,
            subcontractor_id: newSubcontractorId,
            work_status: result.resetToPreliminar ? 'preliminar_tilldelad' : 'tilldelad'
          })
      }
    }

    // Send SMS to TGs
    const smsPromises = dateChangeModalData.tgList
      .filter(tg => tg.phone && result.sendSmsTo[tg.id])
      .map(async tg => {
        const action = result.tgActions[tg.id]
        const identifier = assignment.assignment_number
          ? `#${assignment.assignment_number} ${assignment.title}`
          : assignment.title
        const originalDateTime = dateChangeModalData.originalDate + (assignment.start_time ? ` ${assignment.start_time.substring(0, 5)}` : '')
        const newDateTime = newDate + (assignment.start_time ? ` ${assignment.start_time.substring(0, 5)}` : '')

        let message: string
        if (result.resetToPreliminar) {
          const extra = result.preliminarMessage?.trim()
          message = `Uppdraget ${identifier} (planerat ${originalDateTime}) behöver planeras om.`
          message += extra ? ` ${extra}` : ' Vi återkopplar när vi vet mera.'
        } else if (action?.action === 'remove') {
          const reason = action.removeReason?.trim()
          message = `Du har tagits bort från uppdraget ${identifier} (planerat ${originalDateTime}).`
          if (reason) message += ` ${reason}`
        } else {
          const extra = action?.extraMessage?.trim()
          message = `Uppdraget ${identifier} har flyttats från ${originalDateTime} till ${newDateTime}.`
          if (extra) message += ` ${extra}`
        }

        return sendSMS({
          message,
          recipient_type: 'subcontractor',
          recipient_id: tg.subcontractorId,
          recipient_name: tg.name,
          recipient_phone: tg.phone!,
          assignment_id: assignment.id
        })
      })

    await Promise.allSettled(smsPromises)

    // If we added a new TG and they have a phone, send them SMS too
    if (mode === 'add' && newSubcontractorId) {
      const { data: newTgData } = await supabase
        .from('subcontractors')
        .select('name, phone')
        .eq('id', newSubcontractorId)
        .single()

      if (newTgData?.phone) {
        const identifier = assignment.assignment_number
          ? `#${assignment.assignment_number} ${assignment.title}`
          : assignment.title
        const dateTime = newDate + (assignment.start_time ? ` kl ${assignment.start_time.substring(0, 5)}` : '')
        const addressPart = assignment.location ? `${assignment.location}, ` : ''

        await sendSMS({
          message: `Du är tilldelad uppdraget ${identifier}. ${addressPart}${dateTime}.`,
          recipient_type: 'subcontractor',
          recipient_id: newSubcontractorId,
          recipient_name: newTgData.name || 'Okänd',
          recipient_phone: newTgData.phone,
          assignment_id: assignment.id
        })
      }
    }

    // Invalidate queries
    queryClient.invalidateQueries({ queryKey: ['planning-assignments'] })
    queryClient.invalidateQueries({ queryKey: ['sidebar-assignments'] })

    toast.success(result.resetToPreliminar
      ? 'Uppdraget är nu preliminärt'
      : mode === 'add'
        ? 'Trädgårdsmästare tillagd'
        : 'Datum uppdaterat')

    // Reset modal state
    setShowDateChangeModal(false)
    setDateChangeModalData(null)
  }, [dateChangeModalData, queryClient])

  // Check if a date is in the past
  const isDateInPast = (date: Date) => {
    return isBefore(startOfDay(date), startOfDay(new Date()))
  }

  // Open add dialog when + is clicked
  const handleAddClick = (subcontractorId?: string, date?: Date) => {
    if (!date) return
    if (isDateInPast(date)) {
      toast.error('Kan inte lägga till uppdrag på datum som har passerat')
      return
    }
    setAddTarget({ subcontractorId, date })
    setAddMode('select')
    setSearchQuery('')
    setShowAddDialog(true)
  }

  // Create new assignment with prefilled data
  const createNewAssignment = () => {
    if (!addTarget) return
    setShowAddDialog(false)
    setModalPrefillSubId(addTarget.subcontractorId || undefined)
    setModalPrefillDate(format(addTarget.date, 'yyyy-MM-dd'))
    setModalAssignmentId('new')
  }

  // Link existing assignment to subcontractor and date
  const linkExistingAssignment = (assignmentId: string) => {
    if (!addTarget) return

    updateAssignmentMutation.mutate({
      assignmentId,
      subcontractorId: addTarget.subcontractorId || null,
      newDate: format(addTarget.date, 'yyyy-MM-dd'),
      mode: 'add'
    })

    setShowAddDialog(false)
    setAddTarget(null)
    setSearchQuery('')
  }

  const closeAddDialog = () => {
    setShowAddDialog(false)
    setAddTarget(null)
    setSearchQuery('')
    setAddMode('select')
  }

  const toggleStatus = (status: string) => {
    setStatusFilter(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    )
  }

  return (
    <>
      {/* Mobile Warning - shown only on small screens */}
      <div className="md:hidden h-full flex flex-col items-center justify-center p-6 bg-[#faf9f7] dark:bg-nature-800">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 mx-auto mb-4 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
            <Calendar className="h-8 w-8 text-amber-600 dark:text-amber-400" />
          </div>
          <h2 className="text-xl font-semibold text-nature-900 dark:text-nature-100 mb-2">
            Använd en större skärm
          </h2>
          <p className="text-nature-600 dark:text-nature-400 mb-4">
            Planeringsvyn med drag-and-drop fungerar bäst på dator eller surfplatta i landskapsläge.
          </p>
          <a
            href="/admin/assignments"
            className="inline-flex items-center text-primary-600 dark:text-primary-400 hover:underline"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Gå till uppdragslistan
          </a>
        </div>
      </div>

      {/* Main Planning View - hidden on small screens */}
      <div className="hidden md:flex h-full flex-col">
      {/* Header */}
      <div className="bg-[#faf9f7] dark:bg-nature-800 border-b border-nature-200 dark:border-nature-700 p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-nature-900 dark:text-nature-100">Planering</h1>
            <p className="text-nature-600 dark:text-nature-400">
              Dra och släpp uppdrag för att planera
            </p>
          </div>

          <div className="flex items-center space-x-3">
            {/* Subcontractor Filter */}
            <div className="relative">
              <button
                onClick={() => setShowSubcontractorFilter(!showSubcontractorFilter)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors bg-emerald-100 text-emerald-800 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:hover:bg-emerald-900/50 ${
                  selectedSubcontractors.length < subcontractors.length ? 'ring-2 ring-emerald-500' : ''
                }`}
              >
                <Leaf className="h-4 w-4" />
                <span>TG ({selectedSubcontractors.length}/{subcontractors.length})</span>
                <ChevronDown className="h-4 w-4" />
              </button>

              {showSubcontractorFilter && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-[#faf9f7] dark:bg-nature-800 border border-nature-200 dark:border-nature-600 rounded-lg shadow-lg z-50 p-4">
                  <h4 className="font-medium text-nature-900 dark:text-nature-100 mb-3">
                    Visa trädgårdsmästare
                  </h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {subcontractors.map(sub => (
                      <label key={sub.id} className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedSubcontractors.includes(sub.id)}
                          onChange={() => toggleSubcontractor(sub.id)}
                          className="w-4 h-4 text-primary-600 rounded"
                        />
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: sub.color }}
                        />
                        <span className="text-nature-700 dark:text-nature-300">{sub.name}</span>
                      </label>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-nature-200 dark:border-nature-700 flex justify-between">
                    <div className="flex space-x-3">
                      <button
                        onClick={() => setSelectedSubcontractors(subcontractors.map(s => s.id))}
                        className="text-sm text-nature-500 hover:text-nature-700"
                      >
                        Välj alla
                      </button>
                      <button
                        onClick={() => setSelectedSubcontractors([])}
                        className="text-sm text-nature-500 hover:text-nature-700"
                      >
                        Avmarkera alla
                      </button>
                    </div>
                    <button
                      onClick={() => setShowSubcontractorFilter(false)}
                      className="btn-primary text-sm px-3 py-1"
                    >
                      Stäng
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Day Filter */}
            <div className="relative">
                <button
                  onClick={() => setShowDayFilter(!showDayFilter)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50 ${
                    selectedDays.length < 7 ? 'ring-2 ring-blue-500' : ''
                  }`}
                >
                  <Calendar className="h-4 w-4" />
                  <span>Dagar ({selectedDays.length}/7)</span>
                  <ChevronDown className="h-4 w-4" />
                </button>

                {showDayFilter && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-[#faf9f7] dark:bg-nature-800 border border-nature-200 dark:border-nature-600 rounded-lg shadow-lg z-50 p-4">
                    <h4 className="font-medium text-nature-900 dark:text-nature-100 mb-3">
                      Visa dagar
                    </h4>
                    <div className="space-y-2">
                      {dayNames.map((name, index) => (
                        <label key={index} className="flex items-center space-x-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedDays.includes(index)}
                            onChange={() => toggleDay(index)}
                            className="w-4 h-4 text-primary-600 rounded"
                          />
                          <span className="text-nature-700 dark:text-nature-300">{name}</span>
                        </label>
                      ))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-nature-200 dark:border-nature-700 flex justify-between">
                      <button
                        onClick={() => setSelectedDays([0, 1, 2, 3, 4, 5, 6])}
                        className="text-sm text-nature-500 hover:text-nature-700"
                      >
                        Välj alla
                      </button>
                      <button
                        onClick={() => setShowDayFilter(false)}
                        className="btn-primary text-sm px-3 py-1"
                      >
                        Stäng
                      </button>
                    </div>
                  </div>
                )}
            </div>

            {/* Status Filter */}
            <div className="relative">
              <button
                onClick={() => setShowStatusFilter(!showStatusFilter)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors bg-purple-100 text-purple-800 hover:bg-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:hover:bg-purple-900/50 ${
                  statusFilter.length < PLANNING_STATUSES.length ? 'ring-2 ring-purple-500' : ''
                }`}
              >
                <Filter className="h-4 w-4" />
                <span>Status ({statusFilter.length}/{PLANNING_STATUSES.length})</span>
                <ChevronDown className="h-4 w-4" />
              </button>

              {showStatusFilter && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-[#faf9f7] dark:bg-nature-800 border border-nature-200 dark:border-nature-600 rounded-lg shadow-lg z-50 p-4">
                  <h4 className="font-medium text-nature-900 dark:text-nature-100 mb-3">
                    Visa statusar
                  </h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {PLANNING_STATUSES.map(status => (
                      <label key={status} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={statusFilter.includes(status)}
                          onChange={() => toggleStatus(status)}
                          className="w-4 h-4 text-primary-600 rounded"
                        />
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getAssignmentStatusBadgeClassesV2(status)}`}>
                          {getAssignmentStatusNameV2(status)}
                        </span>
                      </label>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-nature-200 dark:border-nature-700 flex justify-between">
                    <div className="flex space-x-3">
                      <button
                        onClick={() => setStatusFilter([...ACTIVE_STATUSES])}
                        className="text-sm text-nature-500 hover:text-nature-700"
                      >
                        Aktiva
                      </button>
                      <button
                        onClick={() => setStatusFilter([...PLANNING_STATUSES])}
                        className="text-sm text-nature-500 hover:text-nature-700"
                      >
                        Alla
                      </button>
                      <button
                        onClick={() => setStatusFilter([])}
                        className="text-sm text-nature-500 hover:text-nature-700"
                      >
                        Avmarkera alla
                      </button>
                    </div>
                    <button
                      onClick={() => setShowStatusFilter(false)}
                      className="btn-primary text-sm px-3 py-1"
                    >
                      Stäng
                    </button>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>

        {/* Week Navigation - Simplified */}
        <div className="flex items-center justify-between">
          {/* Left: Week navigation + buttons */}
          <div className="flex items-center space-x-2">
            <button
              onClick={navigatePrevious}
              className="p-2 hover:bg-nature-100 dark:hover:bg-nature-700 rounded-lg transition-colors"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <span className="text-lg font-semibold text-nature-900 dark:text-nature-100 min-w-[100px] text-center">
              Vecka {currentWeekNumber}
            </span>
            <button
              onClick={navigateNext}
              className="p-2 hover:bg-nature-100 dark:hover:bg-nature-700 rounded-lg transition-colors"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            <button
              onClick={goToToday}
              className="px-3 py-1.5 text-sm font-medium bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 rounded-lg hover:bg-primary-200 dark:hover:bg-primary-800 transition-colors"
            >
              Idag
            </button>
            <button
              onClick={() => setShowScheduleModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 rounded-lg hover:bg-primary-200 dark:hover:bg-primary-800 transition-colors"
            >
              <Mail className="h-4 w-4" />
              Skicka schema
            </button>

            {/* Week note toggle */}
            <button
              onClick={() => setShowWeekNote(!showWeekNote)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                weekNote
                  ? 'bg-amber-100 text-amber-800 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:hover:bg-amber-900/50'
                  : 'bg-nature-100 text-nature-600 hover:bg-nature-200 dark:bg-nature-700 dark:text-nature-400 dark:hover:bg-nature-600'
              }`}
              title="Veckonotering"
            >
              <FileText className="h-4 w-4" />
              Notering
            </button>

            {/* Collapse/Expand all buttons */}
            <div className="flex items-center ml-2 border-l border-nature-300 dark:border-nature-600 pl-2">
              <button
                onClick={() => setCollapsedSubcontractors(new Set(visibleSubcontractors.map(s => s.id)))}
                className="inline-flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-nature-600 dark:text-nature-400 hover:bg-nature-100 dark:hover:bg-nature-700 rounded transition-colors"
                title="Kollapsa alla TG-rader"
              >
                <ChevronsUpDown className="h-3.5 w-3.5" />
                Kollapsa
              </button>
              <button
                onClick={() => setCollapsedSubcontractors(new Set())}
                className="inline-flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-nature-600 dark:text-nature-400 hover:bg-nature-100 dark:hover:bg-nature-700 rounded transition-colors"
                title="Expandera alla TG-rader"
              >
                <ChevronsUpDown className="h-3.5 w-3.5" />
                Expandera
              </button>
            </div>
          </div>

          {/* Right: Date range */}
          <span className="text-lg font-semibold text-nature-900 dark:text-nature-100">
            {format(displayWeekStart, 'yyyy-MM-dd')} – {format(addDays(displayWeekStart, 6), 'yyyy-MM-dd')}
          </span>
        </div>
      </div>

      {/* Week Note Panel */}
      {showWeekNote && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-700 px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <span className="text-sm font-medium text-amber-800 dark:text-amber-300">
                Notering för vecka {currentWeekNumber}
              </span>
            </div>
            {weekNoteSaved && (
              <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                <Check className="h-3 w-3" />
                Sparad
              </span>
            )}
          </div>
          <textarea
            value={weekNote}
            onChange={(e) => { setWeekNote(e.target.value); setWeekNoteSaved(false) }}
            onBlur={saveWeekNote}
            placeholder="Skriv en notering för denna vecka..."
            className="w-full rounded-md border border-amber-300 dark:border-amber-600 bg-white dark:bg-nature-800 text-nature-900 dark:text-nature-100 px-3 py-2 text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400 placeholder:text-nature-400"
            rows={2}
          />
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar with tabs */}
        <div className={`${showBacklog ? 'w-[28rem]' : 'w-12'} flex-shrink-0 bg-[#faf9f7] dark:bg-nature-800 border-r border-nature-200 dark:border-nature-700 transition-all duration-300 flex flex-col`}>
          <button
            onClick={() => setShowBacklog(!showBacklog)}
            className="p-3 flex items-center justify-between hover:bg-nature-50 dark:hover:bg-nature-700 border-b border-nature-200 dark:border-nature-700"
          >
            {showBacklog ? (
              <>
                <div className="flex items-center space-x-2">
                  <Calendar className="h-5 w-5 text-primary-500" />
                  <span className="font-semibold text-nature-900 dark:text-nature-100">Uppdrag</span>
                  <span className="bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-2 py-0.5 rounded-full text-xs font-medium">
                    {sidebarAssignments.length}
                  </span>
                </div>
                <ChevronLeft className="h-5 w-5 text-nature-400" />
              </>
            ) : (
              <div className="flex flex-col items-center">
                <Calendar className="h-5 w-5 text-primary-500" />
                <span className="text-xs text-primary-600 font-medium mt-1">{sidebarAssignments.length}</span>
              </div>
            )}
          </button>

          {showBacklog && (
            <>
              {/* Sidebar Tabs */}
              <div className="flex border-b border-nature-200 dark:border-nature-700">
                <button
                  onClick={() => setSidebarTab('selected_week')}
                  className={`flex-1 px-2 py-2 text-xs font-medium text-center border-b-2 transition-colors ${
                    sidebarTab === 'selected_week'
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-nature-500 hover:text-nature-700'
                  }`}
                >
                  V.{currentWeekNumber}
                  <span className="ml-1 text-nature-400">({filteredSidebarCounts.selected_week})</span>
                </button>
                <button
                  onClick={() => setSidebarTab('unplanned')}
                  className={`flex-1 px-2 py-2 text-xs font-medium text-center border-b-2 transition-colors ${
                    sidebarTab === 'unplanned'
                      ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                      : 'border-transparent text-nature-500 hover:text-nature-700'
                  }`}
                >
                  Utan datum
                  <span className="ml-1 text-nature-400">({filteredSidebarCounts.unplanned})</span>
                </button>
                <button
                  onClick={() => setSidebarTab('unassigned')}
                  className={`flex-1 px-2 py-2 text-xs font-medium text-center border-b-2 transition-colors ${
                    sidebarTab === 'unassigned'
                      ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                      : 'border-transparent text-nature-500 hover:text-nature-700'
                  }`}
                >
                  Ej tilldelade
                  <span className="ml-1 text-nature-400">({filteredSidebarCounts.unassigned})</span>
                </button>
              </div>

              {/* Sidebar Content */}
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {(() => {
                  const assignments = groupedSidebarAssignments[sidebarTab]
                  const filtered = statusFilter.length > 0
                    ? assignments.filter(a => statusFilter.includes(a.status))
                    : assignments

                  // Sort by status order (using PLANNING_STATUSES constant)
                  const sorted = [...filtered].sort((a, b) => {
                    const aIndex = PLANNING_STATUSES.indexOf(a.status as AssignmentStatusV2)
                    const bIndex = PLANNING_STATUSES.indexOf(b.status as AssignmentStatusV2)
                    return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex)
                  })

                  return sorted.length > 0 ? (
                    sorted.map(assignment => (
                      <SidebarCard
                        key={assignment.id}
                        assignment={assignment}
                        onDragStart={() => setDraggedAssignment(assignment)}
                        onDragEnd={() => setDraggedAssignment(null)}
                        onAssignmentClick={openAssignmentDetail}
                      />
                    ))
                  ) : (
                    <div className="text-center py-8 text-nature-400">
                      <CheckCircle2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">
                        {statusFilter.length < PLANNING_STATUSES.length
                          ? 'Inga uppdrag matchar filtret'
                          : sidebarTab === 'unplanned'
                            ? 'Inga oplanerade uppdrag'
                            : sidebarTab === 'unassigned'
                              ? 'Inga ej tilldelade uppdrag'
                              : `Inga uppdrag vecka ${currentWeekNumber}`}
                      </p>
                    </div>
                  )
                })()}
              </div>
            </>
          )}
        </div>

        {/* Planning Grid */}
        <div className="flex-1 overflow-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nature-600"></div>
            </div>
          ) : (
            /* Week Grid View */
            <div className="min-w-max">
              {/* Header Row - Days */}
              <div className="flex mb-2 sticky top-0 bg-nature-50 dark:bg-nature-900 z-10 pb-2">
                {/* Corner - Drop zone to remove TGs when dragging (not for planned assignments) */}
                <div
                  className={`w-48 flex-shrink-0 mr-2 rounded-lg transition-all ${
                    draggedAssignment && !needsDateChangeModal(draggedAssignment.status)
                      ? 'border-2 border-dashed border-red-400 dark:border-red-500 bg-red-50 dark:bg-red-900/20 cursor-pointer'
                      : ''
                  }`}
                  onDragOver={(e) => {
                    if (draggedAssignment && !needsDateChangeModal(draggedAssignment.status)) {
                      e.preventDefault()
                      e.currentTarget.classList.add('bg-red-100', 'dark:bg-red-900/40')
                    }
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.classList.remove('bg-red-100', 'dark:bg-red-900/40')
                  }}
                  onDrop={(e) => {
                    e.preventDefault()
                    e.currentTarget.classList.remove('bg-red-100', 'dark:bg-red-900/40')
                    if (draggedAssignment && !needsDateChangeModal(draggedAssignment.status)) {
                      handleRemoveAllTGs()
                    }
                  }}
                >
                  {draggedAssignment && !needsDateChangeModal(draggedAssignment.status) && (
                    <div className="h-full flex flex-col items-center justify-center text-red-600 dark:text-red-400 p-2">
                      <UserMinus className="h-6 w-6 mb-1" />
                      <span className="text-xs font-medium text-center">Släpp här för att ta bort alla TG</span>
                    </div>
                  )}
                </div>

                {/* Day Headers */}
                {weekDays.map(day => {
                  const holidayInfo = getHolidayForDate(day)
                  const isRedDay = holidayInfo?.isRedDay || false
                  const isSunday = day.getDay() === 0

                  return (
                    <div
                      key={day.toISOString()}
                      className={`w-56 flex-shrink-0 mr-2 p-3 rounded-lg text-center ${
                        isToday(day)
                          ? 'bg-primary-100 dark:bg-primary-900/30 border-2 border-primary-400'
                          : isRedDay || isSunday
                          ? 'bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700'
                          : 'bg-nature-100 dark:bg-nature-700'
                      }`}
                    >
                      <div className={`text-sm font-medium ${
                        isToday(day)
                          ? 'text-primary-700 dark:text-primary-300'
                          : isRedDay || isSunday
                          ? 'text-red-600 dark:text-red-400'
                          : 'text-nature-600 dark:text-nature-400'
                      }`}>
                        {format(day, 'EEEE', { locale: sv })}
                      </div>
                      <div className={`text-lg font-bold ${
                        isToday(day)
                          ? 'text-primary-900 dark:text-primary-100'
                          : isRedDay || isSunday
                          ? 'text-red-700 dark:text-red-300'
                          : 'text-nature-900 dark:text-nature-100'
                      }`}>
                        {format(day, 'yyyy-MM-dd')}
                      </div>
                      {holidayInfo && (
                        <div className="text-xs text-red-600 dark:text-red-400 mt-1 font-medium">
                          {holidayInfo.name}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              {/* Subcontractor Rows */}
              {visibleSubcontractors.map(sub => {
                const isCollapsed = collapsedSubcontractors.has(sub.id)
                const weekTotal = weekDays.reduce((total, day) => total + getHoursForCell(sub.id, day), 0)

                if (isCollapsed) {
                  // Collapsed compact row
                  return (
                    <div key={sub.id} className="flex mb-1">
                      {/* Compact Subcontractor Header */}
                      <div
                        className="w-48 flex-shrink-0 mr-2 px-3 py-2 rounded-lg flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
                        style={{ backgroundColor: `${sub.color}15`, borderLeft: `4px solid ${sub.color}` }}
                        onClick={() => setCollapsedSubcontractors(prev => {
                          const next = new Set(prev)
                          next.delete(sub.id)
                          return next
                        })}
                      >
                        <ChevronDown className="h-4 w-4 text-nature-400 flex-shrink-0" />
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: sub.color }}
                        />
                        <span className="font-medium text-nature-900 dark:text-nature-100 truncate text-sm">
                          {sub.name}
                        </span>
                        <span className="text-xs text-nature-500 ml-auto flex-shrink-0">
                          {weekTotal.toFixed(1)}h
                        </span>
                      </div>

                      {/* Compact Day Cells - just hours */}
                      {weekDays.map(day => {
                        const hours = getHoursForCell(sub.id, day)
                        const isUnavailable = !isSubcontractorAvailable(sub.id, day)
                        return (
                          <div
                            key={day.toISOString()}
                            className={`w-56 flex-shrink-0 mr-2 px-2 py-2 rounded-lg flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity ${
                              isUnavailable
                                ? 'bg-nature-100 dark:bg-nature-800 opacity-50'
                                : hours > 0
                                  ? 'bg-white dark:bg-nature-700 border border-nature-200 dark:border-nature-600'
                                  : 'bg-nature-50 dark:bg-nature-800/50'
                            }`}
                            onClick={() => setCollapsedSubcontractors(prev => {
                              const next = new Set(prev)
                              next.delete(sub.id)
                              return next
                            })}
                          >
                            {hours > 0 ? (
                              <span className="text-sm font-medium text-nature-700 dark:text-nature-300">
                                {hours.toFixed(1)}h
                              </span>
                            ) : (
                              <span className="text-sm text-nature-300 dark:text-nature-600">—</span>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )
                }

                // Expanded row (default)
                return (
                  <div key={sub.id} className="flex mb-2">
                    {/* Subcontractor Header */}
                    <div
                      className="w-48 flex-shrink-0 mr-2 p-3 rounded-lg flex items-center space-x-3"
                      style={{ backgroundColor: `${sub.color}15`, borderLeft: `4px solid ${sub.color}` }}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <div
                          className="w-4 h-4 rounded-full flex-shrink-0"
                          style={{ backgroundColor: sub.color }}
                        />
                        <button
                          onClick={() => setCollapsedSubcontractors(prev => {
                            const next = new Set(prev)
                            next.add(sub.id)
                            return next
                          })}
                          className="text-nature-400 hover:text-nature-600 dark:hover:text-nature-300 transition-colors"
                          title="Kollapsa rad"
                        >
                          <ChevronUp className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-nature-900 dark:text-nature-100 truncate">
                            {sub.name}
                          </span>
                          {tgScheduleStatus[sub.id]?.allSent && (
                            <span className="inline-flex items-center gap-0.5 px-1 py-0.5 text-[10px] font-medium rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300" title="Schema skickat">
                              <Mail className="h-2.5 w-2.5" />
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-nature-500">
                          {weekTotal.toFixed(1)}h denna vecka
                        </div>
                        {/* Availability days */}
                        <div className="flex gap-0.5 mt-1">
                          {['M', 'T', 'O', 'T', 'F', 'L', 'S'].map((dayLetter, index) => {
                            const isAvailable = sub.available_days?.[index] ?? (index < 5)
                            return (
                              <span
                                key={index}
                                className={`w-4 h-4 flex items-center justify-center text-[9px] font-medium rounded ${
                                  isAvailable
                                    ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300'
                                    : 'bg-nature-100 dark:bg-nature-700 text-nature-400 dark:text-nature-500'
                                }`}
                                title={['Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lördag', 'Söndag'][index]}
                              >
                                {dayLetter}
                              </span>
                            )
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Day Cells */}
                    {weekDays.map(day => (
                      <DropCell
                        key={day.toISOString()}
                        subcontractorId={sub.id}
                        date={day}
                        assignments={getAssignmentsForCell(sub.id, day)}
                        hours={getHoursForCell(sub.id, day)}
                        onDrop={() => handleDrop(sub.id, day)}
                        onDragStart={setDraggedAssignment}
                        onDragEnd={() => setDraggedAssignment(null)}
                        onAssignmentClick={openAssignmentDetail}
                        onAddClick={() => handleAddClick(sub.id, day)}
                        isDragging={!!draggedAssignment}
                        onMarkAsPlanned={handleOpenPlanningModal}
                        onMarkAsPreliminary={handleOpenRevertModal}
                        draggedAssignment={draggedAssignment}
                        isUnavailable={!isSubcontractorAvailable(sub.id, day)}
                      />
                    ))}
                  </div>
                )
              })}

            </div>
          )}
        </div>
      </div>

      {/* Move/Add Dialog */}
      {showMoveDialog && moveTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={closeMoveDialog} />
          <div className="relative bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow-xl p-6 w-96 max-w-[90vw]">
            {needsDateChangeModal(moveTarget.assignment.status) ? (
              // For planned assignments - only allow adding, not replacing
              <>
                <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100 mb-4">
                  Lägg till trädgårdsmästare
                </h3>
                <p className="text-nature-600 dark:text-nature-400 mb-2">
                  Uppdraget "<strong>{moveTarget.assignment.title}</strong>" är planerat och har redan tilldelade trädgårdsmästare.
                </p>
                <p className="text-nature-600 dark:text-nature-400 mb-6">
                  Du kan lägga till en extra trädgårdsmästare. För att ersätta befintliga, gå till uppdragssidan.
                </p>

                <div className="flex flex-col space-y-3">
                  <button
                    onClick={() => handleMoveDialogAction('add')}
                    className="btn-primary w-full py-3"
                  >
                    Lägg till trädgårdsmästare
                  </button>
                  <button
                    onClick={closeMoveDialog}
                    className="text-nature-500 hover:text-nature-700 dark:hover:text-nature-300 py-2"
                  >
                    Avbryt
                  </button>
                </div>
              </>
            ) : (
              // For preliminary assignments - allow both options
              <>
                <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100 mb-4">
                  Flytta eller lägg till?
                </h3>
                <p className="text-nature-600 dark:text-nature-400 mb-2">
                  Uppdraget "<strong>{moveTarget.assignment.title}</strong>" har redan en tilldelad trädgårdsmästare.
                </p>
                <p className="text-nature-600 dark:text-nature-400 mb-6">
                  Vill du flytta uppdraget eller lägga till en extra trädgårdsmästare?
                </p>

                <div className="flex flex-col space-y-3">
                  <button
                    onClick={() => handleMoveDialogAction('add')}
                    className="btn-primary w-full py-3"
                  >
                    Lägg till (behåll befintlig)
                  </button>
                  <button
                    onClick={() => handleMoveDialogAction('move')}
                    className="btn-secondary w-full py-3"
                  >
                    Flytta (ersätt befintlig)
                  </button>
                  <button
                    onClick={closeMoveDialog}
                    className="text-nature-500 hover:text-nature-700 dark:hover:text-nature-300 py-2"
                  >
                    Avbryt
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Unavailability Warning Modal */}
      {showUnavailableWarning && unavailableDropData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={handleUnavailableCancel} />
          <div className="relative bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow-xl p-6 w-96 max-w-[90vw]">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <span className="text-xl">⚠️</span>
              </div>
              <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                {unavailableDropData.reason ? 'Trädgårdsmästaren är ej tillgänglig' : 'Trädgårdsmästaren jobbar inte denna dag'}
              </h3>
            </div>
            <p className="text-nature-600 dark:text-nature-400 mb-6">
              {unavailableDropData.reason ? (
                <>
                  <strong>{unavailableDropData.subcontractorName}</strong> är inte tillgänglig {format(unavailableDropData.date, 'yyyy-MM-dd')}.
                  <br />
                  <span className="inline-block mt-2 px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded text-sm">
                    {unavailableDropData.reason}
                  </span>
                </>
              ) : (
                <>
                  <strong>{unavailableDropData.subcontractorName}</strong> jobbar normalt inte på <strong>{unavailableDropData.dayName}ar</strong>.
                </>
              )}
              <br /><br />
              Vill du ändå lägga uppdraget här?
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleUnavailableCancel}
                className="btn-secondary flex-1 py-2"
              >
                Avbryt
              </button>
              <button
                onClick={handleUnavailableConfirm}
                className="btn-primary flex-1 py-2"
              >
                Ja, lägg ändå
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Link Dialog */}
      {showAddDialog && addTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={closeAddDialog} />
          <div className="relative bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow-xl p-6 w-[480px] max-w-[95vw] max-h-[80vh] flex flex-col">
            {addMode === 'select' ? (
              <>
                <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100 mb-2">
                  Lägg till uppdrag
                </h3>
                <p className="text-nature-600 dark:text-nature-400 mb-6">
                  {format(addTarget.date, 'EEEE d MMMM', { locale: sv })}
                  {addTarget.subcontractorId && subcontractors.find(s => s.id === addTarget.subcontractorId) && (
                    <> • {subcontractors.find(s => s.id === addTarget.subcontractorId)?.name}</>
                  )}
                </p>

                <div className="flex flex-col space-y-3">
                  <button
                    onClick={() => setAddMode('search')}
                    className="btn-secondary w-full py-4 flex items-center justify-center space-x-3"
                  >
                    <Link className="h-5 w-5" />
                    <span>Koppla befintligt uppdrag</span>
                  </button>
                  <button
                    onClick={createNewAssignment}
                    className="btn-primary w-full py-4 flex items-center justify-center space-x-3"
                  >
                    <Plus className="h-5 w-5" />
                    <span>Skapa nytt uppdrag</span>
                  </button>
                  <button
                    onClick={closeAddDialog}
                    className="text-nature-500 hover:text-nature-700 dark:hover:text-nature-300 py-2"
                  >
                    Avbryt
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center space-x-3 mb-4">
                  <button
                    onClick={() => setAddMode('select')}
                    className="p-1 hover:bg-nature-100 dark:hover:bg-nature-700 rounded"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                    Koppla befintligt uppdrag
                  </h3>
                </div>

                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-nature-400" />
                  <input
                    type="text"
                    placeholder="Sök på titel, kund eller uppdragsnummer..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-nature-300 dark:border-nature-600 rounded-lg bg-white dark:bg-nature-700 text-nature-900 dark:text-nature-100 focus:ring-2 focus:ring-primary-500"
                    autoFocus
                  />
                </div>

                <div className="flex-1 overflow-y-auto space-y-2 min-h-[200px] max-h-[400px]">
                  {searchableAssignments.length > 0 ? (
                    searchableAssignments.map(assignment => (
                      <button
                        key={assignment.id}
                        onClick={() => linkExistingAssignment(assignment.id)}
                        className="w-full text-left p-3 rounded-lg border border-nature-200 dark:border-nature-600 hover:bg-nature-50 dark:hover:bg-nature-700 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-nature-900 dark:text-nature-100 truncate">
                              {assignment.assignment_number && `#${assignment.assignment_number} - `}
                              {assignment.title}
                            </div>
                            <div className="text-sm text-nature-500 truncate">
                              {assignment.customers?.full_name || 'Ingen kund'}
                            </div>
                            <div className="text-xs text-nature-400 mt-1 flex items-center space-x-3">
                              <span className="flex items-center">
                                <Calendar className="h-3 w-3 mr-1" />
                                {format(new Date(assignment.start_date), 'd MMM', { locale: sv })}
                              </span>
                              <span className="flex items-center">
                                <Clock className="h-3 w-3 mr-1" />
                                {assignment.start_time?.slice(0, 5)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="text-center py-8 text-nature-400">
                      {searchQuery ? (
                        <p>Inga uppdrag hittades</p>
                      ) : (
                        <p>Inga oplanerade uppdrag tillgängliga</p>
                      )}
                    </div>
                  )}
                </div>

                <button
                  onClick={closeAddDialog}
                  className="mt-4 text-nature-500 hover:text-nature-700 dark:hover:text-nature-300 py-2"
                >
                  Avbryt
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Confirm Planning Modal */}
      {planningModalAssignment && (
        <ConfirmPlanningModal
          isOpen={showPlanningModal}
          onClose={() => {
            setShowPlanningModal(false)
            setPlanningModalAssignment(null)
            setPlanningModalTgInfo([])
          }}
          assignmentNumber={planningModalAssignment.assignment_number?.toString()}
          assignmentTitle={planningModalAssignment.title}
          assignmentDate={planningModalAssignment.start_date || ''}
          assignmentAddress={planningModalAssignment.location || undefined}
          startTime={planningModalAssignment.start_time}
          customerName={planningModalAssignment.customers?.full_name || 'Ingen kund'}
          customerConfirmed={true}
          tgList={planningModalTgInfo}
          onConfirm={handleConfirmPlanning}
        />
      )}

      {/* Revert to Preliminary Modal */}
      {revertModalAssignment && revertModalCustomerInfo && (
        <RevertToPreliminaryModal
          isOpen={showRevertModal}
          onClose={() => {
            setShowRevertModal(false)
            setRevertModalAssignment(null)
            setRevertModalCustomerInfo(null)
            setRevertModalCustomerId(null)
            setRevertModalTgInfo([])
          }}
          assignmentNumber={revertModalAssignment.assignment_number}
          assignmentTitle={revertModalAssignment.title}
          assignmentDate={revertModalAssignment.start_date}
          startTime={revertModalAssignment.start_time}
          customerName={revertModalCustomerInfo.name}
          customerCurrentStatus={revertModalCustomerInfo.status}
          tgList={revertModalTgInfo}
          onConfirm={handleConfirmRevert}
        />
      )}

      {/* Date Change Modal (for moving planned assignments or adding TG) */}
      {dateChangeModalData && (
        <DateChangeRebookingModal
          isOpen={showDateChangeModal}
          onClose={() => {
            setShowDateChangeModal(false)
            setDateChangeModalData(null)
          }}
          originalDate={dateChangeModalData.originalDate}
          newDate={dateChangeModalData.newDate}
          startTime={dateChangeModalData.assignment.start_time}
          assignmentNumber={dateChangeModalData.assignment.assignment_number?.toString()}
          assignmentTitle={dateChangeModalData.assignment.title}
          customerName={dateChangeModalData.customerInfo.name}
          customerCurrentStatus={dateChangeModalData.customerInfo.status}
          currentAssignmentStatus={dateChangeModalData.assignment.status}
          tgList={dateChangeModalData.tgList}
          onConfirm={handleConfirmDateChange}
        />
      )}

      {/* Send Schedule Modal */}
      <SendScheduleModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        weekStart={displayWeekStart}
        weekEnd={addDays(displayWeekStart, 6)}
        assignments={assignments}
        subcontractors={subcontractors}
        onScheduleSent={() => {
          queryClient.invalidateQueries({ queryKey: ['planning-assignments'] })
        }}
      />

      {/* Assignment Detail Modal */}
      {modalAssignmentId && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setModalAssignmentId(null)
              setModalPrefillSubId(undefined)
              setModalPrefillDate(undefined)
              queryClient.invalidateQueries({ queryKey: ['planning-assignments'] })
              queryClient.invalidateQueries({ queryKey: ['sidebar-assignments'] })
            }
          }}
        >
          <div
            className="absolute inset-0"
            onClick={() => {
              setModalAssignmentId(null)
              setModalPrefillSubId(undefined)
              setModalPrefillDate(undefined)
              queryClient.invalidateQueries({ queryKey: ['planning-assignments'] })
              queryClient.invalidateQueries({ queryKey: ['sidebar-assignments'] })
            }}
          />
          <div className="relative bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow-xl w-full max-w-5xl max-h-[95vh] overflow-y-auto">
            <AssignmentDetail
              assignmentId={modalAssignmentId}
              isModal={true}
              prefillSubcontractorId={modalPrefillSubId}
              prefillDate={modalPrefillDate}
              onClose={() => {
                setModalAssignmentId(null)
                setModalPrefillSubId(undefined)
                setModalPrefillDate(undefined)
                queryClient.invalidateQueries({ queryKey: ['planning-assignments'] })
                queryClient.invalidateQueries({ queryKey: ['sidebar-assignments'] })
              }}
              onSaved={(newId) => {
                setModalAssignmentId(newId)
                setModalPrefillSubId(undefined)
                setModalPrefillDate(undefined)
                queryClient.invalidateQueries({ queryKey: ['planning-assignments'] })
                queryClient.invalidateQueries({ queryKey: ['sidebar-assignments'] })
              }}
              onDeleted={() => {
                setModalAssignmentId(null)
                setModalPrefillSubId(undefined)
                setModalPrefillDate(undefined)
                queryClient.invalidateQueries({ queryKey: ['planning-assignments'] })
                queryClient.invalidateQueries({ queryKey: ['sidebar-assignments'] })
              }}
            />
          </div>
        </div>
      )}

      </div>
    </>
  )
}

// Sidebar Card Component - uses status color
interface SidebarCardProps {
  assignment: Assignment
  onDragStart: () => void
  onDragEnd: () => void
  onAssignmentClick?: (id: string) => void
}

function SidebarCard({ assignment, onDragStart, onDragEnd, onAssignmentClick }: SidebarCardProps) {
  const navigate = useNavigate()
  const statusColor = getAssignmentStatusColorV2(assignment.status as AssignmentStatusV2)
  const assignedSubcontractors = assignment.assignment_subcontractors?.filter(as => as.subcontractors) || []

  const handleNavigateToAssignment = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onAssignmentClick) {
      onAssignmentClick(assignment.id)
    } else {
      navigate(`/admin/assignments/${assignment.id}`)
    }
  }

  const handleNavigateToCustomer = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (assignment.customer_id) {
      navigate(`/admin/customers/${assignment.customer_id}`)
    }
  }

  const handleNavigateToSubcontractor = (e: React.MouseEvent, subcontractorId: string) => {
    e.stopPropagation()
    navigate(`/admin/subcontractors/${subcontractorId}`)
  }

  const handleOpenMaps = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (assignment.location) {
      const encodedAddress = encodeURIComponent(assignment.location)
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank')
    }
  }

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className="bg-white dark:bg-nature-700 rounded-lg border border-nature-200 dark:border-nature-600 p-3 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow border-l-4"
      style={{ borderLeftColor: statusColor }}
    >
      <div className="flex items-start space-x-2">
        <GripVertical className="h-4 w-4 text-nature-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          {/* ID - clickable */}
          {assignment.assignment_number && (
            <button
              onClick={handleNavigateToAssignment}
              className="text-xs text-primary-600 dark:text-primary-400 hover:underline mb-0.5"
            >
              #{assignment.assignment_number}
            </button>
          )}
          {/* Title - clickable */}
          <button
            onClick={handleNavigateToAssignment}
            className="font-medium text-nature-900 dark:text-nature-100 text-sm truncate mb-1 block text-left hover:text-primary-600 dark:hover:text-primary-400 w-full"
          >
            {assignment.title}
          </button>

          {/* Status badge */}
          <div className="mb-1">
            <span className={`text-xs px-2 py-0.5 rounded-full ${getAssignmentStatusBadgeClassesV2(assignment.status as AssignmentStatusV2)}`}>
              {getAssignmentStatusNameV2(assignment.status as AssignmentStatusV2)}
            </span>
          </div>

          {/* Description */}
          {assignment.description && (
            <div className="text-xs text-nature-600 dark:text-nature-400 mb-2 line-clamp-2">
              {assignment.description}
            </div>
          )}

          {/* Customer with icon - name is link */}
          <div className="flex items-center text-xs text-nature-500 dark:text-nature-400 mb-1">
            <User className="h-3 w-3 mr-1 flex-shrink-0" />
            {assignment.customer_id ? (
              <button
                onClick={handleNavigateToCustomer}
                className="truncate text-primary-600 dark:text-primary-400 hover:underline"
              >
                {assignment.customers?.full_name || 'Ingen kund'}
              </button>
            ) : (
              <span className="truncate">Ingen kund</span>
            )}
          </div>

          {/* Address with icon - clickable for maps */}
          <div className="flex items-center text-xs text-nature-500 dark:text-nature-400 mb-1">
            <MapPin className="h-3 w-3 mr-1 flex-shrink-0" />
            {assignment.location ? (
              <button
                onClick={handleOpenMaps}
                className="truncate text-primary-600 dark:text-primary-400 hover:underline text-left"
              >
                {assignment.location}
              </button>
            ) : (
              <span className="truncate">Ingen adress</span>
            )}
          </div>

          {/* Subcontractor with leaf icon - names are links, or unassigned badge */}
          {assignedSubcontractors.length > 0 ? (
            <div className="flex items-center text-xs text-nature-500 dark:text-nature-400 mb-1">
              <Leaf className="h-3 w-3 mr-1 flex-shrink-0 text-green-600" />
              <div className="truncate">
                {assignedSubcontractors.map((as, index) => (
                  <span key={as.subcontractor_id}>
                    {index > 0 && ', '}
                    <button
                      onClick={(e) => handleNavigateToSubcontractor(e, as.subcontractor_id)}
                      className="text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      {as.subcontractors?.name}
                    </button>
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center text-xs text-orange-600 dark:text-orange-400 mb-1">
              <AlertTriangle className="h-3 w-3 mr-1 flex-shrink-0" />
              <span className="font-medium">Ej tilldelad</span>
            </div>
          )}

          {/* Date and time */}
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center text-xs text-nature-500">
              <Calendar className="h-3 w-3 mr-1" />
              {assignment.start_date ? format(new Date(assignment.start_date), 'yyyy-MM-dd') : 'Ej planerat'}
            </div>
            <div className="flex items-center text-xs text-nature-500">
              <Clock className="h-3 w-3 mr-1" />
              {assignment.start_time?.slice(0, 5) || '--:--'}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Drop Cell Component
interface DropCellProps {
  subcontractorId: string | null
  date: Date
  assignments: Assignment[]
  hours?: number
  onDrop: () => void
  onDragStart: (assignment: Assignment) => void
  onDragEnd: () => void
  onAssignmentClick: (id: string) => void
  onAddClick: () => void
  isDragging: boolean
  onMarkAsPlanned: (assignment: Assignment) => void
  onMarkAsPreliminary: (assignment: Assignment) => void
  draggedAssignment: Assignment | null
  isUnavailable?: boolean  // TG not available this day
}

function DropCell({
  assignments,
  hours,
  onDrop,
  onDragStart,
  onDragEnd,
  onAssignmentClick,
  onAddClick,
  isDragging,
  onMarkAsPlanned,
  onMarkAsPreliminary,
  draggedAssignment,
  date,
  subcontractorId,
  isUnavailable = false
}: DropCellProps) {
  const [isOver, setIsOver] = useState(false)

  // Check if the dragged assignment is currently in this cell
  const hasSubcontractors = draggedAssignment?.assignment_subcontractors &&
    draggedAssignment.assignment_subcontractors.length > 0
  const hasDate = draggedAssignment?.start_date

  const matchesSubcontractor = draggedAssignment?.assignment_subcontractors?.some(
    as => as.subcontractor_id === subcontractorId
  )
  const matchesDate = hasDate && isSameDay(new Date(draggedAssignment.start_date), date)

  // Highlight logic:
  // - Has date + TG: highlight specific cell
  // - Has date, no TG: highlight all cells on that date
  // - Has TG, no date: highlight all cells for that TG
  const isDraggedAssignmentHere = draggedAssignment && (
    (hasDate && hasSubcontractors && matchesDate && matchesSubcontractor) ||
    (hasDate && !hasSubcontractors && matchesDate) ||
    (!hasDate && hasSubcontractors && matchesSubcontractor)
  )

  return (
    <div
      className={`w-56 flex-shrink-0 mr-2 min-h-[120px] rounded-lg p-2 transition-colors relative ${
        isOver && isDragging
          ? 'bg-primary-100 dark:bg-primary-900/30 border-2 border-dashed border-primary-400'
          : isDraggedAssignmentHere
          ? 'bg-amber-100 dark:bg-amber-900/30 border-2 border-amber-400 ring-2 ring-amber-300 dark:ring-amber-600'
          : isUnavailable
          ? 'bg-nature-100 dark:bg-nature-800 border border-nature-300 dark:border-nature-600 opacity-60'
          : 'bg-nature-50 dark:bg-nature-700/30 border border-nature-200 dark:border-nature-600'
      }`}
      onDragOver={(e) => {
        e.preventDefault()
        setIsOver(true)
      }}
      onDragLeave={() => setIsOver(false)}
      onDrop={(e) => {
        e.preventDefault()
        setIsOver(false)
        onDrop()
      }}
    >
      {/* Hours indicator */}
      {hours !== undefined && hours > 0 && (
        <div className={`text-xs font-medium mb-1 ${
          hours > 8 ? 'text-red-600' : 'text-nature-500'
        }`}>
          {hours.toFixed(1)}h
        </div>
      )}

      {/* Assignments */}
      {assignments.length > 0 ? (
        <div className="space-y-1">
          {assignments.map(assignment => (
            <MiniAssignmentCard
              key={assignment.id}
              assignment={assignment}
              onDragStart={() => onDragStart(assignment)}
              onDragEnd={onDragEnd}
              onClick={() => onAssignmentClick(assignment.id)}
              onMarkAsPlanned={onMarkAsPlanned}
              onMarkAsPreliminary={onMarkAsPreliminary}
            />
          ))}
        </div>
      ) : (
        <div className="h-full flex items-center justify-center">
          {isDragging ? (
            <span className="text-xs text-primary-500">Släpp här</span>
          ) : (
            <button
              onClick={onAddClick}
              className="p-2 rounded-lg text-nature-400 hover:text-nature-600 hover:bg-nature-100 dark:hover:bg-nature-600 transition-colors"
            >
              <Plus className="h-5 w-5" />
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// Mini Assignment Card Component - uses status color
interface MiniAssignmentCardProps {
  assignment: Assignment
  onDragStart: () => void
  onDragEnd: () => void
  onClick: () => void
  onMarkAsPlanned?: (assignment: Assignment) => void
  onMarkAsPreliminary?: (assignment: Assignment) => void
}

function MiniAssignmentCard({ assignment, onDragStart, onDragEnd, onClick, onMarkAsPlanned, onMarkAsPreliminary }: MiniAssignmentCardProps) {
  const canMarkAsPlanned = assignment.status === 'preliminar_planering'
  const canMarkAsPreliminary = assignment.status === 'planerad'

  const handleMarkAsPlanned = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onMarkAsPlanned) {
      onMarkAsPlanned(assignment)
    }
  }

  const handleMarkAsPreliminary = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onMarkAsPreliminary) {
      onMarkAsPreliminary(assignment)
    }
  }

  const handleNavigate = (e: React.MouseEvent) => {
    e.stopPropagation()
    onClick()
  }

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className="bg-white dark:bg-nature-800 rounded p-2 cursor-grab active:cursor-grabbing hover:shadow-sm transition-shadow text-xs"
    >
      {/* ID - clickable */}
      {assignment.assignment_number && (
        <button
          onClick={handleNavigate}
          className="text-[10px] text-primary-600 dark:text-primary-400 hover:underline"
        >
          #{assignment.assignment_number}
        </button>
      )}
      <div className="flex items-center justify-between gap-1">
        <button
          onClick={handleNavigate}
          className="font-medium text-nature-900 dark:text-nature-100 truncate flex-1 text-left hover:text-primary-600 dark:hover:text-primary-400"
        >
          {assignment.title}
        </button>
        <div className="flex items-center gap-1 flex-shrink-0">
          {canMarkAsPlanned && onMarkAsPlanned && (
            <button
              onClick={handleMarkAsPlanned}
              className="p-1 rounded hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600 dark:text-green-400"
              title="Bekräfta planering"
            >
              <Check className="h-3 w-3" />
            </button>
          )}
          {canMarkAsPreliminary && onMarkAsPreliminary && (
            <button
              onClick={handleMarkAsPreliminary}
              className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400"
              title="Ändra till preliminär"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
      {/* Status badge */}
      <div className="mt-1">
        <span
          className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${getAssignmentStatusBadgeClassesV2(assignment.status as AssignmentStatusV2)}`}
        >
          {getAssignmentStatusNameV2(assignment.status as AssignmentStatusV2)}
        </span>
      </div>
      {/* Customer */}
      {assignment.customers?.full_name && (
        <div className="text-nature-500 truncate flex items-center mt-1">
          <User className="h-3 w-3 mr-1 flex-shrink-0" />
          <span className="truncate">{assignment.customers.full_name}</span>
        </div>
      )}
      {/* Address */}
      {assignment.location && (
        <div className="text-nature-500 truncate flex items-center mt-1">
          <MapPin className="h-3 w-3 mr-1 flex-shrink-0" />
          <span className="truncate">{assignment.location}</span>
        </div>
      )}
      <div className="text-nature-400 flex items-center mt-1">
        <Clock className="h-3 w-3 mr-1" />
        {assignment.start_time?.slice(0, 5)}-{assignment.end_time?.slice(0, 5)}
      </div>
    </div>
  )
}

export default Planning
